import { EndPoint, DataProvider } from 'data/Provider';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Label, Input, FormGroup, Button } from 'reactstrap';
import Select from 'react-select';
import { Typeahead } from "react-bootstrap-typeahead";
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import classNames from 'classnames';
import { CustomView } from 'types/CustomView';
import { DataContext } from 'hooks/HeaderContext';

const distanceOptions = [{
    label: '10 miles',
    value: '10'
},
{
    label: '25 miles',
    value: '25'
}, {
    label: '50 miles',
    value: '50'
}, {
    label: '100 miles',
    value: '100'
}, {
    label: '150 miles',
    value: '150'
}
]



interface SearchPanelProps {
    isOpen: boolean;
    toggleSearchPanel(): void;

}


const SearchPanel: FC<SearchPanelProps> = ({ isOpen, toggleSearchPanel }) => {
    const { customView, commitViewForm, userNearestCity } = useContext(DataContext);
    const [citiesList, setCitiesList] = useState(null);
    const [zipcodesList, setZipcodesList] = useState(null);
    const [locationError, setLocationError] = useState(false);
    const [form, setForm] = useState<CustomView>(customView);


    useEffect(() => { setForm(customView) }, [customView]);

    const locationList: string[] = useMemo(() => {
        if (citiesList && zipcodesList) return citiesList.concat(zipcodesList)
    }, [citiesList, zipcodesList]);




    const setUseMyLocation = (useLocation: boolean) => {
        if (useLocation) {
            setForm(f => ({ range: '100', ...f, selection: 'location' }))
        }
    };

    const setUseCityZip = (useCityZip: boolean) => {
        if (useCityZip) {
            setLocationError(false);
            setForm(f => ({ range: '100', ...f, selection: 'cityZip', location: null }))
        }
    };

    const setViewAllDogs = (viewAllDogs: boolean) => {
        if (viewAllDogs) {
            setLocationError(false);
            setForm(null)
        }
    };


    const getRangeOption = (val: string) => {
        return distanceOptions.filter(d => d.value === val);
    }

    // on page load calls retrieveCities and retrieveZipcodes functions to initialize typeahead data
    useEffect(() => {
        retrieveCities();
        retrieveZipcodes();
    }, []);

    async function retrieveZipcodes() {
        // retrieve json info
        try {
            const res = await DataProvider.get(EndPoint.ZIPCODES);
            let zipcodes = await res.data as string[];
            setZipcodesList(zipcodes.map(i => String(i)));
        } catch (err) {
            console.error('Failed to fetch zipcodes.', err)
        }
    }

    async function retrieveCities() {
        try {
            const res = await DataProvider.get(EndPoint.CITIES);
            let cities = await res.data;
            setCitiesList(cities);
        } catch (err) {
            console.error('Failed to fetch cities.', err)
        }
    }

    const changeCityZip = async (loc: any[]) => {
        if (loc.length > 0 && locationList.find(l => l === loc[0])) {
            setForm(f => ({ ...f, location: { cityZip: loc[0] } }))
        } else {
            setForm(f => ({ ...f, location: null }))
        }
        setLocationError(false);
    }

    const submit = () => {
        if (form && form.selection === 'cityZip' && !form.location) {
            setLocationError(true)
            return false;
        }
        commitViewForm(form);
        toggleSearchPanel();
    }



    return (
        <Alert
            isOpen={isOpen}
            color='secondary'
            className="container-lg p-3 searchDiv"
        >
            <div className=''>
                <div className='text-center mb-2'>
                    <strong>View Dogs:</strong>
                </div>
                <div className="d-flex">
                    <div className='mx-auto'>
                        <Label className='' htmlFor='viewAllRadio'>
                            <Input type='radio'
                                name="location-filter-radios"
                                id="viewAllRadio"
                                className="me-2"
                                checked={!form}
                                onChange={_ => setViewAllDogs(true)}
                                autoFocus
                                tabIndex={0}
                            />
                            Throughout Missouri
                        </Label>
                        <div className='mb-0'>

                            <Label className='' htmlFor='locationRadio'>
                                <Input type='radio'
                                    name="location-filter-radios"
                                    id="locationRadio"
                                    className="me-2"
                                    checked={form && form.selection === 'location'}
                                    onChange={_ => setUseMyLocation(true)}
                                    tabIndex={0}
                                />
                                Near my Location
                            </Label>
                        </div>
                        <FormGroup>
                            <Label className='' htmlFor='cityZipRadio'>
                                <Input type='radio'
                                    name="location-filter-radios"
                                    id='cityZipRadio'
                                    className="me-2"
                                    checked={form && form.selection === 'cityZip'}
                                    onChange={_ => setUseCityZip(true)}
                                    tabIndex={0}
                                />
                                Near CITY or ZIP CODE
                            </Label>

                        </FormGroup>
                    </div>
                </div>
                {form?.selection === 'location' &&
                    < div className="mb-4 col-lg-6 mx-lg-auto d-flex flex-column">

                        <div className="text-center h6 text-dark fst-italic">near {userNearestCity}</div>
                    </div>
                }
                {form?.selection === 'cityZip' &&
                    <div className="mb-4 col-lg-6 mx-lg-auto d-flex flex-column">

                        <div className="text-center h6 text-dark fw-bold">Location</div>
                        <div className='position-relative mx-auto' style={{ width: '300px' }}>
                            <Typeahead
                                className='location-typeahead'
                                options={locationList}
                                placeholder='Enter Missouri City or ZIP'
                                id='location-typeahead'
                                multiple={false}
                                minLength={1}
                                clearButton={form && form?.location?.cityZip != null}
                                selectHint={(shouldSelect, event) => (
                                    event.key === "Enter" || shouldSelect
                                )}
                                isInvalid={locationError}

                                selected={(form && form.location && form.location.cityZip && [form?.location?.cityZip]) || []}
                                onInputChange={loc => changeCityZip([loc])}
                                onChange={loc => changeCityZip(loc)}
                            />
                            <i className={classNames('bi bi-geo-alt-fill position-absolute font-primary',
                                {
                                    'font-primary': !locationError,
                                    'text-danger': locationError
                                })}
                                style={{ left: '10px', top: '11px', backgroundColor: 'white' }}
                            ></i>
                            <div className='invalid-feedback'>Location required.</div>
                        </div>
                    </div>
                }
                {form &&
                    <div className="mb-5 col-lg-6 mx-lg-auto d-flex flex-column align-items-center">
                        <div style={{ width: '300px' }}>
                            <div className="text-center h6 text-dark fw-bold">Distance</div>
                            <Select
                                classNamePrefix='p4p-select'
                                options={distanceOptions}
                                value={getRangeOption(form.range ? form.range : '100')}
                                onChange={distance => setForm(f => ({ ...f, range: distance.value }))}
                            />
                        </div>
                    </div>
                }
                <div className=' col-lg-6 mx-lg-auto d-flex justify-content-center mt-3'>
                    <Button
                        className='rounded-pill w-50'
                        color='primary'
                        onClick={() => submit()}
                    >
                        GO
                    </Button>
                </div>
            </div>

        </Alert >


    )
}

export default SearchPanel;




















